import { APIHomeBinderData } from 'types/api.types';
import {
  LocationData,
  RatingInfoField,
  MortgageeInfo,
} from '../components/HomeLocationCard';
import type { HomeBinderSectionData } from './types';

export const processHomeLocationsData = (
  binderData: APIHomeBinderData,
): HomeBinderSectionData<{ locations: LocationData[] }> => {
  const applicationData = binderData?.application_data;
  const dwellings = applicationData?.dwellings;

  if (!dwellings) {
    return {
      title: 'Locations',
      description: 'Location and dwelling details',
      isValid: true,
      data: { locations: [] },
    };
  }

  // Process each dwelling into location data
  const locations = [dwellings].flat().map((dwelling) => {
    const riskAddress = dwelling?.risk_address;
    const ratingInfo = dwelling?.rating_information;
    const mortgagees = dwelling?.mortgagee || [];
    const coverages = dwelling?.coverages;

    // Format risk address
    const addressParts = [
      riskAddress?.address,
      riskAddress?.city,
      riskAddress?.province,
      riskAddress?.postal_code,
    ].filter(Boolean);
    const formattedAddress = addressParts.join(', ');

    // Build rating information fields
    const ratingFields: RatingInfoField[] = [];

    // Basic information
    if (ratingInfo?.year_built) {
      ratingFields.push({ field: 'Year', value: ratingInfo.year_built });
    }
    if (ratingInfo?.number_of_storeys) {
      ratingFields.push({
        field: 'Storeys',
        value: ratingInfo.number_of_storeys,
      });
    }
    if (ratingInfo?.number_of_families) {
      ratingFields.push({
        field: 'No. of Families',
        value: ratingInfo.number_of_families,
      });
    }
    if (ratingInfo?.number_of_units) {
      ratingFields.push({
        field: 'No. of Units',
        value: ratingInfo.number_of_units,
      });
    }
    if (ratingInfo?.total_living_area) {
      ratingFields.push({
        field: 'Total Living Area',
        value: `${ratingInfo.total_living_area.area} ${ratingInfo.total_living_area.type}`,
      });
    }
    if (ratingInfo?.access_type) {
      ratingFields.push({
        field: 'Access Type',
        value: ratingInfo.access_type,
      });
    }
    if (
      ratingInfo?.has_smokers !== undefined &&
      ratingInfo?.has_smokers !== null
    ) {
      ratingFields.push({
        field: 'Smokers',
        value: ratingInfo.has_smokers ? 'True' : 'False',
      });
    }
    if (ratingInfo?.occupancy_type) {
      ratingFields.push({
        field: 'Occupancy Type',
        value: ratingInfo.occupancy_type,
      });
    }
    if (ratingInfo?.structure_type) {
      ratingFields.push({
        field: 'Structure Type',
        value: ratingInfo.structure_type,
      });
    }
    if (ratingInfo?.foundation_type) {
      ratingFields.push({
        field: 'Foundation Type',
        value: ratingInfo.foundation_type,
      });
    }
    if (ratingInfo?.finished_basement_percent) {
      ratingFields.push({
        field: 'Finished Basement',
        value: ratingInfo.finished_basement_percent,
      });
    }
    if (ratingInfo?.exterior_wall_framing_type) {
      ratingFields.push({
        field: 'Exterior Wall Framing Type',
        value: ratingInfo.exterior_wall_framing_type,
      });
    }
    if (ratingInfo?.exterior_wall_finish_type) {
      ratingFields.push({
        field: 'Exterior Wall Finish Type',
        value: ratingInfo.exterior_wall_finish_type,
      });
    }

    // Upgrades
    if (ratingInfo?.upgrades) {
      const upgrades = ratingInfo.upgrades;
      ratingFields.push({
        field: 'Roof Upgrades',
        value: `Full(YY): ${upgrades.roof?.full_year || ''}\nPartial(YY): ${
          upgrades.roof?.partial_year || ''
        }`,
        section: 'upgrades',
      });
      ratingFields.push({
        field: 'Electrical Upgrades',
        value: `Full(YY): ${
          upgrades.electrical?.full_year || ''
        }\nPartial(YY): ${upgrades.electrical?.partial_year || ''}`,
        section: 'upgrades',
      });
      ratingFields.push({
        field: 'Heating Upgrades',
        value: `Full(YY): ${upgrades.heating?.full_year || ''}\nPartial(YY): ${
          upgrades.heating?.partial_year || ''
        }`,
        section: 'upgrades',
      });
      ratingFields.push({
        field: 'Plumbing Upgrades',
        value: `Full(YY): ${upgrades.plumbing?.full_year || ''}\nPartial(YY): ${
          upgrades.plumbing?.partial_year || ''
        }`,
        section: 'upgrades',
      });
    }

    // Heating and Electrical
    if (ratingInfo?.roof_covering_type) {
      ratingFields.push({
        field: 'Roof Covering Type',
        value: ratingInfo.roof_covering_type,
        section: 'heating',
      });
    }
    if (ratingInfo?.electrical_wiring_type) {
      ratingFields.push({
        field: 'Electrical Wiring Type',
        value: ratingInfo.electrical_wiring_type,
        section: 'heating',
      });
    }
    if (ratingInfo?.electrical_panel_type) {
      ratingFields.push({
        field: 'Electrical Panel Type',
        value: ratingInfo.electrical_panel_type,
        section: 'heating',
      });
    }
    if (ratingInfo?.service_amps) {
      ratingFields.push({
        field: 'Service',
        value: ratingInfo.service_amps,
        section: 'heating',
      });
    }
    if (ratingInfo?.primary_heating_type) {
      const heating = ratingInfo.primary_heating_type;
      if (heating.apparatus) {
        ratingFields.push({
          field: 'Primary Heating Apparatus',
          value: heating.apparatus,
          section: 'heating',
        });
      }
      if (heating.fuel) {
        ratingFields.push({
          field: 'Primary Heating Fuel',
          value: heating.fuel,
          section: 'heating',
        });
      }
      if (heating.location) {
        ratingFields.push({
          field: 'Primary Heating Location',
          value: heating.location,
          section: 'heating',
        });
      }
    }

    // Format mortgagees
    const formattedMortgagees: MortgageeInfo[] = mortgagees.map((m) => {
      const mortgageAddressParts = [
        m.address,
        m.city,
        m.province_code,
        m.postal_code,
      ].filter(Boolean);
      return {
        name: m.name || 'N/A',
        address: mortgageAddressParts.join(', ') || 'N/A',
      };
    });

    return {
      locationNumber: dwelling?.loc_nr || '1',
      riskAddress: formattedAddress || 'N/A',
      coverageFormType: coverages?.coverage_form_type || 'N/A',
      ratingInfo: ratingFields,
      mortgagees: formattedMortgagees,
    };
  });

  return {
    title: 'Locations',
    description: 'Location and dwelling details',
    isValid: true,
    data: { locations },
  };
};
